import React from "react";
import {
  Server,
  MemoryStick,
  Bot,
  Search,
  Eraser,
  ScanEye,
  FileCheck2,
  CloudUpload,
  Printer,
  Power,
} from "lucide-react";

const PIECES = [
  {
    n: "01",
    icon: Server,
    title: "Orchestrator",
    sub: "On the bench controller",
    body: "Presents the secure eraser to each machine and moves down the line — fully hands-off after the first power-on.",
  },
  {
    n: "02",
    icon: MemoryStick,
    title: "Secure eraser environment",
    sub: "Runs in the machine's memory",
    body: "Each machine boots into a RAM-based erasing OS, so the drive can be safely wiped and the controller can move on to the next machine.",
  },
  {
    n: "03",
    icon: Bot,
    title: "Erasure agent",
    sub: "Runs inside the eraser",
    body: "Detects drives, erases each with the right method, verifies, signs the certificate, uploads it, prints a label, and powers the machine off.",
  },
];

const AGENT_STEPS = [
  { icon: Search, label: "Detect drives" },
  { icon: Eraser, label: "Erase" },
  { icon: ScanEye, label: "Verify" },
  { icon: FileCheck2, label: "Sign certificate" },
  { icon: CloudUpload, label: "Upload to cloud" },
  { icon: Printer, label: "Print label" },
  { icon: Power, label: "Power off" },
];

const AutomatedWorkflow = () => {
  return (
    <section id="workflow" className="relative bg-white font-body">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-brand-bg px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            Zero-touch workflow
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
            Connect Once. <span className="text-brand-gradient">Walk Away.</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink sm:text-lg">
            Built for a zero-touch refurbishment line. A worker connects a batch of machines once and powers
            them on — the system handles the rest. Dozens of devices can be processed in parallel, each
            producing its own certificate.
          </p>
        </div>

        {/* three pieces */}
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {PIECES.map(({ n, icon: Icon, title, sub, body }, i) => (
            <div
              key={title}
              className="relative flex flex-col rounded-2xl border border-line bg-white p-6 transition-shadow hover:shadow-xl hover:shadow-brand-plum/5"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-wash text-brand-magenta ring-1 ring-brand-orchid/30">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="font-display text-2xl font-semibold text-brand-wash2">{n}</span>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-brand-plum">{title}</h3>
              <p className="text-xs font-medium uppercase tracking-wide text-brand-magenta">{sub}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted">{body}</p>
              {i < PIECES.length - 1 && (
                <span className="absolute -right-3 top-1/2 z-10 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-white text-brand-orchid md:flex">
                  ↓
                </span>
              )}
            </div>
          ))}
        </div>

        {/* agent step rail */}
        <div className="mt-10 rounded-3xl border border-line bg-gradient-to-br from-brand-bg to-white p-6 sm:p-8">
          <p className="text-sm font-semibold text-brand-plum">Inside the agent — every step, automatic:</p>
          <div className="mt-5 flex flex-wrap items-center gap-y-3">
            {AGENT_STEPS.map(({ icon: Icon, label }, i) => (
              <React.Fragment key={label}>
                <div className="flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-2 shadow-sm">
                  <Icon className="h-4 w-4 text-brand-magenta" />
                  <span className="whitespace-nowrap text-xs font-medium text-brand-plum">{label}</span>
                </div>
                {i < AGENT_STEPS.length - 1 && (
                  <span className="mx-1.5 text-brand-orchid">→</span>
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-5 text-sm leading-relaxed text-muted">
            No one babysits the bench. The result: a clean device, a printed label, and a signed certificate —
            every machine, every time.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AutomatedWorkflow;
